import { useState } from 'react';
import { Button, Group, Paper, Text } from '@mantine/core';
import { FileText, Star } from 'react-feather';
import type { AiPromptSelectionsDoc } from '../../../../services/api/coachSessionPrep';
import { coachSessionPrepApi } from '../../../../services/api/coachSessionPrep';

/**
 * MED-876 AC1 — AI suggestions banner shown at the top of the Session Agenda.
 *
 * Visual references: screenshots #4 (in context) and #6 (close-up).
 *
 * - Hidden when `count === 0` (per AC1: "the banner shows the count of AI
 *   prompts currently marked In Use" — no count, no banner).
 * - Hidden when the parent passes `aiFeaturesEnabled=false` (per IV1 —
 *   parent gates rendering; this component assumes it's on).
 */
export interface AISuggestionsBannerProps {
  sessionId: string;
  selections: AiPromptSelectionsDoc;
  onShowAll?: () => void;
  /**
   * Whether to render the "View Prep Sheet" action. Defaults to true (coach).
   * Peer leader prep (MED-909 AC5) has no prep-sheet PDF, so it passes false.
   */
  showPrepSheet?: boolean;
}

/** Sum of all slots where `in_use === true` across all segments. */
export function countInUseSelections(doc: AiPromptSelectionsDoc): number {
  let count = 0;
  for (const segment of Object.values(doc)) {
    if (segment.purpose?.in_use) count++;
    if (segment.speaking?.in_use) count++;
    if (segment.stall_recovery?.in_use) count++;
  }
  return count;
}

export function AISuggestionsBanner({
  sessionId,
  selections,
  onShowAll,
  showPrepSheet = true,
}: AISuggestionsBannerProps) {
  const [prepSheetLoading, setPrepSheetLoading] = useState(false);
  const count = countInUseSelections(selections);
  if (count === 0) return null;

  // MED-1104: openPrepSheetPdf is async (authenticated blob fetch). Track a
  // loading state so the button shows a spinner and resists double-clicks.
  const handleViewPrepSheet = async () => {
    setPrepSheetLoading(true);
    try {
      await coachSessionPrepApi.openPrepSheetPdf(sessionId);
    } finally {
      setPrepSheetLoading(false);
    }
  };

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      style={{ backgroundColor: '#f8f7ff', borderColor: '#e0d8ff' }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          <Star size={16} color="#7c4dff" />
          <Text size="sm" style={{ lineHeight: 1.4 }}>
            <Text span fw={600}>
              {count} AI prompt{count === 1 ? '' : 's'}
            </Text>{' '}
            selected for your prep sheet — these will also surface during the live session
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {onShowAll && (
            <Button size="xs" variant="subtle" onClick={onShowAll}>
              Show all
            </Button>
          )}
          {showPrepSheet && (
            <Button
              size="xs"
              color="dark"
              leftSection={<FileText size={12} />}
              loading={prepSheetLoading}
              onClick={handleViewPrepSheet}
            >
              View Prep Sheet
            </Button>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

export default AISuggestionsBanner;
